import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Grid, Card, CardActionArea, CardContent, Avatar, Skeleton, Stack, Button, Paper } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { PieChart } from '@mui/x-charts/PieChart';

// Icons
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PieChartIcon from '@mui/icons-material/PieChart';
import StoreIcon from '@mui/icons-material/Store';
import AddIcon from '@mui/icons-material/Add';

import useAuthStore from '../store/useAuthStore';
import { getAggregatedReport } from '../services/mwcService';

const ALL_MENU_ITEMS = [
  {
    title: 'Monitoring Ranting',
    description: 'Pantau setoran ranting.',
    path: '/mwc/monitor-ranting',
    icon: <VisibilityIcon fontSize="large" />,
    color: '#ed6c02', 
    roles: ['MANAJERIAL']
  },
  {
    title: 'Laporan Agregat',
    description: 'Analisis MWC & Ranting.',
    path: '/mwc/aggregated-report',
    icon: <AssessmentIcon fontSize="large" />,
    color: '#0288d1', 
    roles: ['MANAJERIAL']
  },
  {
    title: 'Transaksi Internal',
    description: 'Input kas operasional.',
    path: '/mwc/transactions',
    icon: <ReceiptIcon fontSize="large" />,
    color: '#2e7d32', 
    roles: ['MANAJERIAL', 'LAPANGAN']
  },
];

const StatWidget = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', boxShadow: 3, borderRadius: 2 }}>
    <Box sx={{ position: 'absolute', top: -15, right: -15, opacity: 0.1, transform: 'rotate(-10deg) scale(2.5)', color: color }}>
        {icon}
    </Box>
    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 64, height: 64, mr: 2.5 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 0.5 }}>{title}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {loading ? <Skeleton width={120} /> : value}
            </Typography>
        </Box>
    </CardContent>
  </Card>
);

const MWCDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const isLapangan = user?.jabatan === 'LAPANGAN';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getAggregatedReport();
        if (!data.mwc) data.mwc = { totalPendapatan: 0, totalBeban: 0, transactionCount: 0 };
        if (!data.ranting) data.ranting = { totalPendapatan: 0, totalBeban: 0, transactionCount: 0 };
        setReportData(data);
      } catch (error) {
        console.error("Gagal memuat dashboard MWC:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isLapangan]);

  const stats = useMemo(() => {
    if (!reportData) return { pendapatan: 0, beban: 0, saldo: 0, transaksi: 0 };
    const p = (parseFloat(reportData.mwc?.totalPendapatan)||0) + (parseFloat(reportData.ranting?.totalPendapatan)||0);
    const b = (parseFloat(reportData.mwc?.totalBeban)||0) + (parseFloat(reportData.ranting?.totalBeban)||0);
    const t = (reportData.mwc?.transactionCount||0) + (reportData.ranting?.transactionCount||0);
    return { pendapatan: p, beban: b, saldo: p - b, transaksi: t };
  }, [reportData]);

  const pieData = useMemo(() => {
    if (!reportData || !reportData.transactions) return [];

    const categoryTotals = {};
    
    reportData.transactions.forEach(tx => {
        if (tx.jenis === 'PEMASUKAN') {
            const catName = tx.kategori?.nama || 'Tanpa Kategori';
            const amount = parseFloat(tx.jumlah);
            
            if (!categoryTotals[catName]) categoryTotals[catName] = 0;
            categoryTotals[catName] += amount;
        }
    });

    const sortedCats = Object.entries(categoryTotals)
        .sort(([, a], [, b]) => b - a)
        .map(([label, value], index) => ({
            id: index,
            value,
            label,
            color: `hsl(${150 + (index * 40)}, 60%, ${40 + (index * 10)}%)` // Greenish/Varied hues similar to LazisnuDashboard
        }));

    return sortedCats;
  }, [reportData]);

  const filteredMenuItems = ALL_MENU_ITEMS.filter(item => {
      if (isLapangan) return item.roles.includes('LAPANGAN');
      return item.roles.includes('MANAJERIAL');
  });

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        
        {/* HEADER & PRIORITY ACTIONS */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box sx={{ mb: {xs: 2, md: 0} }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                    Dashboard MWC
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {isLapangan 
                    ? `Halo ${user?.username}, berikut adalah ringkasan kinerja saat ini.` 
                    : `Selamat Datang, ${user?.username || 'Admin'}. Pantau kinerja MWC dan Ranting.`}
                </Typography>
            </Box>
            
            {/* Priority Button for Lapangan */}
            {isLapangan && (
                <Button 
                    variant="contained" 
                    color="success" 
                    size="large" 
                    startIcon={<AddIcon />} 
                    onClick={() => navigate('/mwc/transactions')}
                    sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 'bold', boxShadow: 3 }}
                >
                    Input Transaksi Baru
                </Button>
            )}
        </Paper>

        {/* HERO STATS */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Pemasukan Gabungan" value={`Rp${stats.pendapatan.toLocaleString('id-ID')}`} icon={<TrendingUpIcon fontSize="large" />} color="#2e7d32" loading={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Pengeluaran Gabungan" value={`Rp${stats.beban.toLocaleString('id-ID')}`} icon={<TrendingDownIcon fontSize="large" />} color="#d32f2f" loading={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Saldo Akhir Gabungan" value={`Rp${stats.saldo.toLocaleString('id-ID')}`} icon={<AccountBalanceWalletIcon fontSize="large" />} color="#1976d2" loading={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatWidget title="Total Transaksi" value={stats.transaksi.toLocaleString('id-ID')} icon={<SwapHorizIcon fontSize="large" />} color="#ed6c02" loading={loading} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
            {/* LEFT COLUMN (Analysis & Logs) */}
            <Grid item xs={12} md={8}>
                {/* 1. Composition Chart */}
                <Card sx={{ boxShadow: 3, borderRadius: 2, mb: 3 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                            <PieChartIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold">Komposisi Pemasukan (Kategori)</Typography>
                        </Stack>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Box sx={{ height: 200, width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    {loading ? <Skeleton variant="circular" width={180} height={180} /> : 
                                     (stats.pendapatan === 0 ? <Typography color="text.secondary">Belum ada data.</Typography> :
                                        <PieChart series={[{ data: pieData, innerRadius: 40, outerRadius: 90, paddingAngle: 2, cornerRadius: 4 }]} width={250} height={200} slotProps={{ legend: { hidden: true } }} />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {!loading && stats.pendapatan > 0 && (
                                    <Stack spacing={2}>
                                        {pieData.map((d) => (
                                            <Box key={d.id} sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                                    <Box sx={{ width: 12, height: 12, bgcolor: d.color, borderRadius: '50%' }} />
                                                    <Typography variant="body2" color="text.secondary">{d.label}</Typography>
                                                </Stack>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {((d.value / stats.pendapatan) * 100).toFixed(1)}%
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Rp{d.value.toLocaleString('id-ID')}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* 2. Recent Activity */}
                {/* Removed Audit Transaksi Internal as requested */}
            </Grid>

            {/* RIGHT COLUMN (Action & Tools) */}
            <Grid item xs={12} md={4}>
                <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'flex', alignItems: 'center', mb: 2 }}>
                            <DashboardIcon sx={{ mr: 1 }} /> Menu Utama
                        </Typography>
                        <Stack spacing={2}>
                            {filteredMenuItems.map((item) => (
                                <Card key={item.title} sx={{ transition: '0.2s', '&:hover': { transform: 'translateX(4px)', boxShadow: 4, borderColor: item.color }, borderRadius: 2, borderLeft: `6px solid ${item.color}` }}>
                                    <CardActionArea component={Link} to={item.path} sx={{ p: 2 }}>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Avatar sx={{ bgcolor: `${item.color}15`, color: item.color }}>
                                                {item.icon}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">{item.title}</Typography>
                                                <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                                            </Box>
                                        </Stack>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Stack>
                        
                        {/* Supervisi Box (Only for Managerial) */}
                        {!isLapangan && (
                            <Box sx={{ mt: 4, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px dashed #2196f3' }}>
                                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                    <StoreIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main">Status Ranting</Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Pastikan seluruh ranting telah melakukan input data sebelum tanggal 5 setiap bulannya.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default MWCDashboard;